import React, { useState, useEffect } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { useAuth } from "../context/AuthContext";
import {
  isDevAuthEnabled,
  devLogin,
  getTestUsers,
  isDevAuthenticated,
  getDevUser,
} from "../services/devAuth";

function LoginPage() {
  const { isAuthenticated, loading, error, login } = useAuth();
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check if user just logged out
  const justLoggedOut = searchParams.get("loggedOut") === "1";

  console.log("[LoginPage] Render - justLoggedOut:", justLoggedOut);
  console.log("[LoginPage] Render - isAuthenticated:", isAuthenticated);
  console.log("[LoginPage] Render - loading:", loading);
  console.log("[LoginPage] Render - accounts:", accounts?.length);

  // Dev mode state
  const [devMode, setDevMode] = useState(false);
  const [testUsers, setTestUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("admin");
  const [devLoading, setDevLoading] = useState(false);
  const [devError, setDevError] = useState(null);
  const [cleaningSession, setCleaningSession] = useState(justLoggedOut);

  useEffect(() => {
    console.log("[LoginPage] useEffect - justLoggedOut:", justLoggedOut);
    console.log("[LoginPage] useEffect - cleaningSession:", cleaningSession);

    // Se l'utente ha appena fatto logout, pulisci completamente la sessione MSAL
    if (justLoggedOut) {
      console.log("[LoginPage] User just logged out, cleaning MSAL session...");
      console.log("[LoginPage] accounts before clear:", accounts?.length);

      // Pulisci tutto lo storage
      localStorage.clear();
      sessionStorage.clear();
      console.log("[LoginPage] Storage cleared");

      // Pulisci la cache MSAL
      instance
        .clearCache()
        .then(() => {
          console.log("[LoginPage] MSAL cache cleared successfully");
          console.log("[LoginPage] accounts after clear:", accounts?.length);
          setCleaningSession(false);
          // Rimuovi il parametro dall'URL
          window.history.replaceState({}, "", "/login");
        })
        .catch((err) => {
          console.warn("[LoginPage] MSAL cache clear failed:", err);
          setCleaningSession(false);
          window.history.replaceState({}, "", "/login");
        });

      return;
    }

    // Check if dev auth is enabled
    if (isDevAuthEnabled()) {
      setDevMode(true);

      // Check if already authenticated in dev mode
      if (isDevAuthenticated()) {
        navigate("/", { replace: true });
        return;
      }

      // Load test users
      getTestUsers()
        .then((data) => setTestUsers(data.users || []))
        .catch((err) => console.warn("Could not load test users:", err));
    }
  }, [navigate, justLoggedOut, instance, accounts]);

  // Handle dev login
  const handleDevLogin = async () => {
    setDevLoading(true);
    setDevError(null);

    try {
      await devLogin(selectedUser);
      // Reload completo: l'AuthProvider rilegge il token dev solo al mount
      // (navigate() client-side non fa ri-renderizzare il provider).
      window.location.replace("/");
    } catch (err) {
      setDevError(err.message);
    } finally {
      setDevLoading(false);
    }
  };

  if (loading || cleaningSession) {
    console.log(
      "[LoginPage] Showing spinner - loading:",
      loading,
      "cleaningSession:",
      cleaningSession,
    );
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="spinner w-12 h-12"></div>
      </div>
    );
  }

  // Non fare redirect automatico se l'utente ha appena fatto logout
  if (isAuthenticated && !justLoggedOut) {
    console.log(
      "[LoginPage] REDIRECTING TO HOME - isAuthenticated:",
      isAuthenticated,
      "justLoggedOut:",
      justLoggedOut,
    );
    return <Navigate to="/" replace />;
  }

  console.log("[LoginPage] Showing login form");

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-600 to-primary-800">
      <div className="bg-white rounded-xl shadow-2xl p-8 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="/logo-phc.jpg"
            alt="Peralba Health Care"
            className="h-20 mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-gray-800 mb-1">
            Peralba Health Care
          </h1>
          <p className="text-lg text-primary-600 font-medium">
            Portale Manutenzioni
          </p>
        </div>

        {/* Error message */}
        {(error || devError) && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error || devError}
          </div>
        )}

        {/* Login info */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm">
          <p className="font-medium mb-1">Accesso al portale</p>
          <p>
            Inserisci il tuo indirizzo email e la tua password per accedere.
            Riceverai un codice di verifica via email per completare il login.
          </p>
        </div>

        {/* Production Login button */}
        <button onClick={login} className="w-full btn-primary py-3 text-lg">
          Accedi con Email
        </button>

        {/* Dev Mode Section */}
        {devMode && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="flex items-center gap-2 mb-4">
              <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded">
                DEV MODE
              </span>
              <span className="text-sm text-gray-500">Accesso di test</span>
            </div>

            {/* User selector */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Seleziona utente di test
              </label>
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                className="form-input w-full"
              >
                {testUsers.length > 0 ? (
                  testUsers.map((user) => (
                    <option key={user.key} value={user.key}>
                      {user.name} ({user.role})
                    </option>
                  ))
                ) : (
                  <>
                    <option value="admin">Admin Test</option>
                    <option value="rspp">RSPP Test</option>
                    <option value="direzione">Direzione Test</option>
                    <option value="direttore_rsa">Direttore RSA Test</option>
                    <option value="amministrativo">Amministrativo Test</option>
                    <option value="fornitore">Fornitore Test</option>
                  </>
                )}
              </select>
            </div>

            {/* Selected user info */}
            {testUsers.length > 0 && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm">
                {(() => {
                  const user = testUsers.find((u) => u.key === selectedUser);
                  if (!user) return null;
                  return (
                    <>
                      <p>
                        <span className="font-medium">Email:</span> {user.email}
                      </p>
                      <p>
                        <span className="font-medium">Ruolo:</span> {user.role}
                      </p>
                      <p>
                        <span className="font-medium">Permessi:</span>{" "}
                        {user.permissions}
                      </p>
                    </>
                  );
                })()}
              </div>
            )}

            {/* Dev Login button */}
            <button
              onClick={handleDevLogin}
              disabled={devLoading}
              className="w-full py-2 px-4 bg-yellow-500 hover:bg-yellow-600 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
            >
              {devLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Accesso...
                </span>
              ) : (
                `Accedi come ${selectedUser}`
              )}
            </button>

            <p className="mt-2 text-xs text-gray-400 text-center">
              Token JWT valido per 24h
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 text-center text-xs text-gray-400">
          <p>Peralba Health Care S.p.A.</p>
          <p className="mt-1">
            Per assistenza rivolgersi a: automation@peralbahc.com
          </p>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
